import React from "react";
import { FormLabel, InputField, SelectField } from "components/FormFields";
import {
  IBasicDetailsFormProps,
  SelectOption,
} from "containers/OnboardingBusiness/types";
import Render from "components/Common/Render";

const BasicDetailsForm: React.FC<IBasicDetailsFormProps> = ({
  values,
  setFieldValue,
  setFieldTouched,
  errors,
  touched,
  communityOptions,
  countryOptions,
  stateOptions,
  cityOptions,
  isLoadingCommunities,
  isLoadingCountries,
  isLoadingStates,
  isLoadingCities,
  onCommunitySelect,
}) => {
  const handleCommunityChange = (selectedOption: any) => {
    setFieldValue("community", selectedOption);
    
    if (onCommunitySelect && selectedOption) {
      // Community selected - auto-fill will happen in onCommunitySelect
      onCommunitySelect(selectedOption);
    } else {
      // Community deselected - clear only the auto-filled fields (Country, State, City)
      setFieldValue("country", null);
      setFieldValue("state", null);
      setFieldValue("city", null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Community Selection Section */}
      <Render when={isLoadingCommunities || communityOptions.length > 0}>
        <div>
          <h2 className="mb-2 sm:mb-3 font-poppins font-semibold text-lg leading-[18px] text-royalPurple">
            Community Selection
          </h2>
          <div>
            <FormLabel>Community</FormLabel>
            <SelectField<SelectOption>
              getOptionLabel={(option) => option?.name || ""}
              getOptionValue={(option) => option?.value || ""}
              value={values.community}
              isDisabled={isLoadingCommunities}
              isLoading={isLoadingCommunities}
              isSearchable
              placeholder={
                isLoadingCommunities
                  ? "Loading communities..."
                  : "Select Community"
              }
              closeMenuOnSelect
              options={communityOptions}
              onChange={handleCommunityChange}
              error={errors.community}
              touched={touched.community}
            />
          </div>
        </div>
      </Render>

      {/* Basic Details Section */}
      <div>
        <h2 className="mb-2 sm:mb-3 font-poppins font-semibold text-lg leading-[18px] text-royalPurple">
          Add Basic Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          {/* Business Name */}
          <InputField
            label="Business Name *"
            type="text"
            placeholder="Enter business name"
            value={values.businessName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("businessName", e.target.value)}
            error={touched.businessName && errors.businessName}
            containerClass="mb-0"
          />

          {/* Street Address */}
          <InputField
            label="Street Address"
            type="text"
            placeholder="Enter street address"
            value={values.streetAddress}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("streetAddress", e.target.value)}
            error={touched.streetAddress && errors.streetAddress}
            containerClass="mb-0"
          />

          {/* Country */}
          <div>
            <FormLabel required>Country</FormLabel>
            <SelectField<SelectOption>
              getOptionLabel={(option) => option?.name || ""}
              getOptionValue={(option) => option?.value || ""}
              value={values.country}
              isSearchable
              placeholder={
                isLoadingCountries ? "Loading countries..." : values.community ? "Auto-filled from community" : "Select country"
              }
              isLoading={isLoadingCountries}
              noOptionsMessage={() =>
                isLoadingCountries ? "Loading..." : "No countries found"
              }
              closeMenuOnSelect
              options={countryOptions}
              onChange={(selectedOption) =>
                setFieldValue("country", selectedOption)
              }
              error={(errors.country as any)?.name || ""}
              touched={touched.country}
            />
          </div>

          {/* State */}
          <div>
            <FormLabel required>State</FormLabel>
            <SelectField<SelectOption>
              getOptionLabel={(option) => option?.name || ""}
              getOptionValue={(option) => option?.value || ""}
              value={values.state}
              isDisabled={!values.country || isLoadingStates}
              isSearchable
              placeholder={
                isLoadingStates ? "Loading states..." : values.community ? "Auto-filled from community" : "Select state"
              }
              isLoading={isLoadingStates}
              noOptionsMessage={() =>
                isLoadingStates ? "Loading..." : "No states found"
              }
              closeMenuOnSelect
              options={stateOptions}
              onChange={(selectedOption) =>
                setFieldValue("state", selectedOption)
              }
              error={(errors.state as any)?.name || ""}
              touched={touched.state}
            />
          </div>

          {/* City */}
          <div>
            <FormLabel required>City</FormLabel>
            <SelectField<SelectOption>
              getOptionLabel={(option) => option?.name || ""}
              getOptionValue={(option) => option?.value || ""}
              value={values.city}
              isDisabled={!values.state || isLoadingCities}
              isSearchable
              placeholder={
                isLoadingCities ? "Loading cities..." : values.community ? "Auto-filled from community" : "Select city"
              }
              isLoading={isLoadingCities}
              noOptionsMessage={() =>
                isLoadingCities ? "Loading..." : "No cities found"
              }
              closeMenuOnSelect
              options={cityOptions}
              onChange={(selectedOption) =>
                setFieldValue("city", selectedOption)
              }
              error={(errors.city as any)?.name || ""}
              touched={touched.city}
            />
          </div>

          {/* Zipcode */}
          <InputField
            label="Zipcode *"
            type="text"
            placeholder="Enter zipcode"
            value={values.zipcode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setFieldValue("zipcode", e.target.value)
            }
            extras={{
              onBlur: () => setFieldTouched("zipcode", true),
            }}
            error={touched.zipcode ? errors.zipcode : undefined}
            containerClass="mb-0"
          />

          {/* Referral Code */}
          <div className="relative">
            <InputField
              label="Referral Code"
              type="text"
              placeholder="Enter referral code"
              value={values.referralCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue("referralCode", e.target.value)}
              extras={{
                onBlur: () => setFieldTouched("referralCode", true),
              }}
              error={touched.referralCode ? errors.referralCode : undefined}
              containerClass="mb-0"
              customClass="pr-12"
            />
            <div className="absolute right-3 top-9 w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center cursor-pointer">
              <span className="text-white text-sm font-bold">J</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicDetailsForm;
